import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Clock, ShieldCheck } from "lucide-react";

import { SiteShell } from "@/components/marketing/site-shell";
import { getCourse } from "@/lib/academy/curriculum";

export const Route = createFileRoute("/academy/$course/")({
  head: ({ params }) => {
    const course = getCourse(params.course);
    const title = course ? `${course.title} — ArosFlow Academy` : "Course — ArosFlow Academy";
    const desc = course?.blurb ?? "ArosFlow Academy course.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...(course ? [] : [{ name: "robots", content: "noindex" }]),
      ],
    };
  },
  component: CoursePage,
});

function CoursePage() {
  const { course: slug } = Route.useParams();
  const course = getCourse(slug);

  if (!course) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6">
          <h1 className="text-2xl font-semibold">Course not found</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            That course link is no longer valid.
          </p>
          <Link to="/academy" className="focus-key mt-6 inline-block text-sm text-gold">
            Back to Academy
          </Link>
        </div>
      </SiteShell>
    );
  }

  const minutes = course.lessons.reduce((s, l) => s + l.minutes, 0);

  return (
    <SiteShell>
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <Link
          to="/academy"
          className="focus-key inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-gold"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Academy
        </Link>

        <h1 className="mt-5 text-2xl font-semibold tracking-tight sm:text-3xl">{course.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{course.blurb}</p>
        <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Clock className="h-3.5 w-3.5" aria-hidden /> {course.lessons.length} lessons · {minutes}{" "}
          min
        </p>

        <ol className="mt-8 space-y-3">
          {course.lessons.map((l, i) => (
            <li key={l.slug}>
              <Link
                to="/academy/$course/$lesson"
                params={{ course: course.slug, lesson: l.slug }}
                className="focus-key flex items-start gap-4 rounded-xl border border-[var(--glass-border)] bg-card p-4 hover:border-[var(--gold)]/40"
              >
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md border border-[var(--gold)]/40 text-xs font-semibold text-gold">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{l.title}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                    {l.summary}
                  </span>
                  <span className="mt-1.5 block text-[11px] text-muted-foreground">
                    {l.minutes} min
                  </span>
                </span>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden />
              </Link>
            </li>
          ))}
        </ol>

        <p className="mt-8 flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" aria-hidden />
          Education only — not legal, tax or investment advice, and no promise of income. Rules
          differ by state; check yours and use a real estate attorney.
        </p>
      </div>
    </SiteShell>
  );
}
